/**
 * „Stały link do rozmów", Integracje's „Rozmowy wideo" group (owner, 28 October 2026; a row since
 * 4 November): the link every video consultation takes when Google makes no Meet. Its host only —
 * the link joins whoever holds it (consultation-calls R7) — with „Zmień" / „Usuń", or the empty
 * state's platform suggestion and „Dodaj link". Editing happens inside the row.
 */

import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { FormBanner } from '~/shared/ui/FormBanner';
import { FormField } from '~/shared/ui/FormField';
import { LinkGlyph } from '~/shared/ui/VideoGlyphs';

import type { StandingCallLinkApi } from '../lib/useStandingCallLink';
import { PRIMARY_PILL, SECONDARY_PILL, DANGER_PILL } from './accountButtons';
import screen from './AccountScreen.module.css';
import styles from './CalendarTab.module.css';

const KEY = 'web:account.calendar.standing';

export function StandingCallLinkBlock({
  standing,
  className,
}: {
  readonly standing: StandingCallLinkApi;
  readonly className: string;
}) {
  const { t } = useTranslation();
  const { view, field } = standing;
  const fieldBox = useRef<HTMLFormElement>(null);
  const opener = useRef<HTMLButtonElement>(null);
  const fieldOpen = field !== null;
  const wasOpen = useRef(false);

  // Into the field when it opens; back to the act that opened it when it closes.
  useEffect(() => {
    if (fieldOpen) fieldBox.current?.querySelector('input')?.focus();
    else if (wasOpen.current) opener.current?.focus();
    wasOpen.current = fieldOpen;
  }, [fieldOpen]);

  if (view === null) return null;

  const mark = (
    <span className={styles.tile} aria-hidden="true">
      <LinkGlyph className={styles.tileGlyph} />
    </span>
  );

  return (
    <div className={`${String(styles.section)} ${className}`}>
      <h2 className={screen.groupLabel}>{t(`${KEY}.group`)}</h2>
      <div className={screen.group} data-clarity-mask="True">
        {field !== null ? (
          <form
            ref={fieldBox}
            className={screen.groupRow}
            noValidate
            onSubmit={(event) => {
              event.preventDefault();
              standing.onSave();
            }}
          >
            {mark}
            <div className={screen.rowBody}>
              <FormField
                inline
                label={t(`${KEY}.fieldLabel`)}
                value={field.value}
                placeholder="https://"
                error={field.problem}
                onChange={standing.onEdit}
              />
            </div>
            <div className={screen.rowActions}>
              <button type="button" className={SECONDARY_PILL} onClick={standing.onClose}>
                {t('common.cancel')}
              </button>
              <button type="submit" className={PRIMARY_PILL} aria-disabled={standing.busy ? 'true' : undefined}>
                {t(`${KEY}.save`)}
              </button>
            </div>
          </form>
        ) : (
          <div className={screen.groupRow}>
            {mark}
            <div className={screen.rowBody}>
              <b className={styles.title}>{t(`${KEY}.title`)}</b>
              <span className={styles.status}>{view.kind === 'set' ? view.host : t(view.bodyKey)}</span>
            </div>
            <div className={screen.rowActions}>
              <button
                ref={opener}
                type="button"
                className={SECONDARY_PILL}
                aria-disabled={standing.busy ? 'true' : undefined}
                onClick={standing.onOpen}
              >
                {t(view.kind === 'set' ? `${KEY}.change` : `${KEY}.add`)}
              </button>
              {view.kind === 'set' ? (
                <button
                  type="button"
                  className={DANGER_PILL}
                  aria-disabled={standing.busy ? 'true' : undefined}
                  onClick={standing.onRemove}
                >
                  {t(`${KEY}.remove`)}
                </button>
              ) : null}
            </div>
          </div>
        )}
        {standing.refusal !== null && (
          <div className={styles.rowBanner}>
            <FormBanner message={standing.refusal} />
          </div>
        )}
      </div>
    </div>
  );
}
